import { ResultOf } from "gql.tada";
import { useLenis } from "lenis/react";
import { IntroBenefitsSectionById } from "../../../queries/entries/IntroBenefitsSectionById";
import { useIsDesktop } from "../../../hooks/useIsDesktop";
import { IntroBenefitPart, PARALLAX_RATE } from "./IntroBenefitParts";
import { useScroll, useTransform, motion } from "motion/react";
import { useRef } from "react";
import { AnimatedResponsiveDetails } from "./AnimatedResponsiveDetails";
import { HighlightingIcon } from "./HighlightingIcon";

export const IntroBenefitsSection = ({
  entry: { introBenefitsSection: entry },
}: {
  entry: ResultOf<typeof IntroBenefitsSectionById>;
}) => {
  const images = entry?.benefitItemsCollection?.items || [];
  const containerRef = useRef<HTMLElement>(null);
  const lenis = useLenis();

  const { scrollYProgress: fillToExitScrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });

  const value = useTransform(
    () => fillToExitScrollYProgress.get() * images.length
  );

  const { scrollYProgress: enterToExitScrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"],
  });

  const isDesktop = useIsDesktop();
  const lastIndex = images.length - 1;

  const lastSlideGoingOut = useTransform(
    value,
    [0, lastIndex, lastIndex + 1],
    [0, 0, 1]
  );

  const isMobile = !useIsDesktop();
  const contentContainerY = useTransform(
    () => `${lastSlideGoingOut.get() * (1 - PARALLAX_RATE) * 100}%`
  );

  const handleIconClick = (index: number) => {
    if (!containerRef?.current || !entry?.iconsCollection?.items?.length) {
      return;
    }
    const relativeTop = containerRef.current.getBoundingClientRect().y;
    const top = relativeTop + window.scrollY;
    const containerHeight = containerRef.current.getBoundingClientRect().height;
    const itemsCount = entry?.iconsCollection?.items?.length;
    lenis?.scrollTo(top + (index * containerHeight) / itemsCount);
  };

  if (!entry) return null;

  return (
    <section className="gap-0 w-full relative" ref={containerRef}>
      <div>
        {images.map((image, index) => (
          <IntroBenefitPart
            value={value}
            key={index}
            image={image?.background?.url || ""}
            total={images.length}
            index={index}
            isDesktop={isDesktop}
            benefitItem={entry.benefitItemsCollection?.items[index] || null}
            title={entry.benefits_title || ""}
          />
        ))}
      </div>
      <div className="absolute inset-0 size-full">
        <div className="sticky top-0 h-screen w-full overflow-hidden">
          <motion.div
            className="size-full grid-container items-center"
            style={{ y: isMobile ? 0 : contentContainerY }}
          >
            {entry.benefitItemsCollection?.items?.map((item, index) => (
              <AnimatedResponsiveDetails
                key={index}
                benefitItem={item}
                total={images.length}
                index={index}
                scrollYProgress={enterToExitScrollYProgress}
              />
            ))}
            <div
              className="dsk:col-start-2 col-span-10"
              style={{ position: "relative", top: "-25%" }}
            >
              <h2 className="absolute text-body-regular">
                {entry.benefits_title}
              </h2>
            </div>
            <div className="col-start-23">
              <div className="col-start-19 flex flex-col items-center justify-center h-full">
                {entry.iconsCollection?.items?.map((icon, index) => (
                  <HighlightingIcon
                    key={index}
                    icon={icon?.iconimage?.url ?? ""}
                    index={index}
                    scrollYProgress={enterToExitScrollYProgress}
                    total={images.length}
                    onClick={handleIconClick}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
